import { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowRight, Quote } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { images } from "@/data/images";
import { NextChapter } from "@/components/NextChapter";
import {
  whyICoach,
  philosophyCards,
  teachingBelief,
  teachingPreview,
  teachingIntro,
  teachingCards,
} from "@/data/philosophy";

const byId = Object.fromEntries(philosophyCards.map((c) => [c.id, c]));
const faith = byId["faith"];
const relationships = byId["relationships"];
const attitude = byId["attitude"];
const standards = byId["standards"];
const leadership = byId["servant-leadership"];
const teachingApproaches = teachingCards;

// Order matters — drives both the sticky nav and the scrollspy.
const CATEGORIES = [
  { id: "faith", label: "Faith" },
  { id: "relationships", label: "Relationships" },
  { id: "attitude", label: "Attitude" },
  { id: "standards", label: "Standards" },
  { id: "servant-leadership", label: "Leadership" },
  { id: "teaching", label: "Teaching" },
];

const Block = ({ block }) =>
  block.type === "quote" ? (
    <blockquote className="my-5 border-l-2 border-burnt pl-5">
      <p className="font-heading text-lg md:text-xl italic text-white leading-snug">
        “{block.text}”
      </p>
      {block.author && (
        <cite className="mt-2 block text-sm text-gold not-italic">{block.author}</cite>
      )}
    </blockquote>
  ) : (
    <p className="text-zinc-400 leading-relaxed max-w-prose">{block.text}</p>
  );

const ReadMore = ({ open, onClick, id }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={open}
    data-testid={`pillar-toggle-${id}`}
    className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white hover:text-burnt transition-colors"
  >
    <span className="border-b border-burnt/60 pb-0.5">{open ? "Show Less" : "Read More"}</span>
    <ChevronDown
      size={15}
      className={`text-burnt transition-transform duration-300 ${open ? "rotate-180" : ""}`}
    />
  </button>
);

const Expandable = ({ open, id, children }) => (
  <AnimatePresence initial={false}>
    {open && (
      <motion.div
        key="c"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <div
          data-testid={`pillar-content-${id}`}
          className="mt-6 border-l-2 border-burnt/40 pl-5 md:pl-6 space-y-3"
        >
          {children}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// Large, photo-led pillar for the most important beliefs.
const FeaturedPillar = ({ id, index, label, belief, preview, image, imageAlt, caption, aspectClass = "aspect-[4/5] md:aspect-[4/3] lg:aspect-[4/5]", reverse, open, onToggle, children }) => (
  <section id={id} className="scroll-mt-32 md:scroll-mt-40 py-14 md:py-24" data-testid={`pillar-${id}`}>
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <div className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
        <div className={`relative ${reverse ? "lg:[direction:ltr]" : ""}`}>
          <Reveal>
            {image ? (
              <div className={`relative overflow-hidden border border-white/10 ${aspectClass}`}>
                <img src={image} alt={imageAlt} loading="lazy" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-base/90 via-ink-base/10 to-ink-base/25" />
                <span className="absolute top-4 right-5 font-heading text-5xl md:text-6xl font-bold text-white/15 leading-none select-none">
                  {index}
                </span>
                {caption && (
                  <p className="absolute bottom-5 left-5 right-5 font-heading text-lg md:text-xl italic text-white leading-snug text-balance">
                    “{caption}”
                  </p>
                )}
              </div>
            ) : (
              <div className={`relative overflow-hidden border border-white/10 bg-ink-surface ${aspectClass} grid place-items-center`}>
                <div className="grain absolute inset-0 opacity-40" />
                <span className="font-heading text-7xl md:text-8xl font-bold text-white/10 leading-none select-none">
                  {index}
                </span>
              </div>
            )}
          </Reveal>
        </div>
        <div className={reverse ? "lg:[direction:ltr]" : ""} data-nav-anchor>
          <Reveal delay={0.06}>
            <p className="label-eyebrow text-burnt mb-4">{label}</p>
            <h2 className="font-heading text-2xl md:text-4xl font-semibold tracking-tight text-white leading-[1.15] text-balance">
              {belief}
            </h2>
            <p className="mt-4 text-zinc-400 leading-relaxed max-w-md">{preview}</p>
            <ReadMore id={id} open={open} onClick={onToggle} />
            <Expandable id={id} open={open}>
              {children}
            </Expandable>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

// Compact card for supporting beliefs.
const SupportPillar = ({ id, index, label, belief, preview, open, onToggle, children }) => (
  <section id={id} className="scroll-mt-32 md:scroll-mt-40" data-testid={`pillar-${id}`}>
    <div className="max-w-4xl mx-auto px-4 md:px-8">
      <div className="group border border-white/10 border-l-2 border-l-burnt/70 bg-ink-surface p-7 md:p-10 transition-colors hover:border-white/20">
        <div className="flex items-baseline gap-4">
          <span className="font-mono text-sm text-burnt shrink-0">{index}</span>
          <div className="flex-1 min-w-0" data-nav-anchor>
            <p className="label-eyebrow text-zinc-500 mb-3">{label}</p>
            <h3 className="font-heading text-xl md:text-2xl font-semibold tracking-tight text-white leading-snug text-balance">
              {belief}
            </h3>
            <p className="mt-3 text-zinc-400 leading-relaxed max-w-xl">{preview}</p>
            <ReadMore id={id} open={open} onClick={onToggle} />
            <Expandable id={id} open={open}>
              {children}
            </Expandable>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const VisualBreak = ({ image, statement }) => (
  <div className="my-6 md:my-10">
    <div className="max-w-7xl mx-auto px-4 md:px-8">
      <Reveal>
        <div className="relative overflow-hidden border border-white/10 h-[240px] md:h-[380px]">
          <img src={image} alt="" loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-base/90 via-ink-base/50 to-transparent" />
          {statement && (
            <div className="absolute inset-0 flex items-center">
              <p className="px-6 md:px-14 font-heading text-2xl md:text-4xl font-light italic text-white max-w-xl leading-snug text-balance">
                “{statement}”
              </p>
            </div>
          )}
        </div>
      </Reveal>
    </div>
  </div>
); // eslint-disable-line no-unused-vars

export default function Philosophy() {
  const [openId, setOpenId] = useState(null);
  const [activeCat, setActiveCat] = useState("faith");
  const navRef = useRef(null);
  const clickLockRef = useRef(false);

  const toggle = useCallback(
    (id) => setOpenId((cur) => (cur === id ? null : id)),
    []
  );

  // Reading line = just below the fixed navbar + sticky category nav.
  const readingLine = () => (window.innerWidth >= 768 ? 150 : 124) + 8;

  const scrollToId = useCallback((id) => {
    const section = document.getElementById(id);
    if (!section) return;
    // Target the eyebrow/heading block (not the whole section) so image-led
    // pillars still land with the category title + headline visible. The anchor
    // has no scroll-margin, so Lenis won't double-count the header offset.
    const target = section.querySelector("[data-nav-anchor]") || section;
    const offset = window.innerWidth >= 768 ? 150 : 124;
    // Set active immediately and lock the scrollspy so passing sections can't
    // override the highlight during the smooth-scroll animation.
    setActiveCat(id);
    clickLockRef.current = true;
    const unlock = () => {
      clickLockRef.current = false;
    };
    const lenis = window.__lenis;
    if (lenis) {
      lenis.scrollTo(target, { offset: -offset, duration: 1.0, onComplete: unlock });
      setTimeout(unlock, 1150); // safety in case onComplete doesn't fire
    } else {
      const y = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setTimeout(unlock, 1000);
    }
  }, []);

  // Scrollspy — single source of truth based on heading position.
  // Active = the last section whose heading anchor has reached the reading line.
  useEffect(() => {
    let ticking = false;
    const compute = () => {
      ticking = false;
      if (clickLockRef.current) return;
      const line = readingLine();
      let current = CATEGORIES[0].id;
      for (const c of CATEGORIES) {
        const section = document.getElementById(c.id);
        if (!section) continue;
        const anchor = section.querySelector("[data-nav-anchor]") || section;
        const top = anchor.getBoundingClientRect().top;
        if (top - line <= 1) current = c.id; // heading has reached/passed the line
        else break; // later headings are further down the page
      }
      setActiveCat((prev) => (prev === current ? prev : current));
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(compute);
      }
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Keep the active pill in view within the horizontal nav.
  useEffect(() => {
    const el = navRef.current?.querySelector(`[data-cat="${activeCat}"]`);
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeCat]);

  return (
    <div data-testid="philosophy-page">
      <style>{`.phil-scroll::-webkit-scrollbar{display:none}`}</style>

      {/* 1 · HERO */}
      <section className="relative overflow-hidden min-h-[52vh] md:min-h-[60vh] flex items-end">
        <div className="absolute inset-0 z-0 bg-ink-base">
          <div className="absolute inset-0 bg-gradient-to-br from-ink-surface via-ink-base to-black" />
          <div className="grain absolute inset-0 opacity-40" />
        </div>
        <span className="pointer-events-none absolute -bottom-6 right-2 md:right-8 font-heading font-bold text-white/[0.04] text-[22vw] leading-none select-none z-0">
          BELIEFS
        </span>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full pt-28 md:pt-36 pb-12 md:pb-16">
          <Reveal>
            <p className="font-mono text-burnt text-sm mb-3">02</p>
            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-white leading-[0.95]">
              My Philosophy
            </h1>
            <p className="mt-5 text-lg md:text-xl text-zinc-300 font-light max-w-xl">
              The beliefs that shape how I coach, teach, and lead.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 2 · STICKY CATEGORY NAV */}
      <nav
        data-testid="philosophy-nav"
        className="sticky top-16 md:top-20 z-40 bg-ink-base/90 backdrop-blur-lg border-y border-white/10"
      >
        <div
          ref={navRef}
          className="phil-scroll max-w-7xl mx-auto px-4 md:px-8 flex gap-2 overflow-x-auto whitespace-nowrap py-3"
          style={{ scrollbarWidth: "none" }}
        >
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-cat={c.id}
              data-testid={`philosophy-nav-${c.id}`}
              onClick={() => scrollToId(c.id)}
              className={`shrink-0 px-4 py-1.5 text-sm font-medium border transition-colors ${
                activeCat === c.id
                  ? "bg-burnt border-burnt text-white"
                  : "border-white/15 text-zinc-400 hover:text-white hover:border-white/40"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </nav>

      {/* 3 · WHY I COACH — opening statement */}
      <section className="py-16 md:py-24" data-testid="philosophy-why">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <Reveal>
            <p className="label-eyebrow text-zinc-500 mb-6">Why I Coach</p>
          </Reveal>
          <div className="space-y-6">
            {whyICoach.map((p, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <p className="font-heading text-xl md:text-2xl font-light text-zinc-200 leading-relaxed text-balance">
                  {p}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAITH — featured */}
      <FeaturedPillar
        id="faith"
        index="01"
        label="Faith"
        belief={faith.belief}
        preview={faith.preview}
        image={images.girlsChampions}
        imageAlt="Coach Elete's team, tournament champions"
        caption="Champions are built long before the trophy."
        aspectClass="aspect-[3/2]"
        open={openId === "faith"}
        onToggle={() => toggle("faith")}
      >
        {faith.blocks.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </FeaturedPillar>

      {/* RELATIONSHIPS — supporting */}
      <SupportPillar
        id="relationships"
        index="02"
        label="Relationships"
        belief={relationships.belief}
        preview={relationships.preview}
        open={openId === "relationships"}
        onToggle={() => toggle("relationships")}
      >
        {relationships.blocks.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </SupportPillar>

      {/* ATTITUDE — supporting */}
      <SupportPillar
        id="attitude"
        index="03"
        label="Attitude"
        belief={attitude.belief}
        preview={attitude.preview}
        open={openId === "attitude"}
        onToggle={() => toggle("attitude")}
      >
        {attitude.blocks.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </SupportPillar>

      {/* STANDARDS — featured */}
      <FeaturedPillar
        id="standards"
        index="04"
        label="Standards"
        belief={standards.belief}
        preview={standards.preview}
        image={images.teamHuddle}
        imageAlt="Coach Elete's team in a huddle"
        reverse
        open={openId === "standards"}
        onToggle={() => toggle("standards")}
      >
        {standards.blocks.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </FeaturedPillar>

      {/* LEADERSHIP — supporting */}
      <SupportPillar
        id="servant-leadership"
        index="05"
        label="Leadership"
        belief={leadership.belief}
        preview={leadership.preview}
        open={openId === "servant-leadership"}
        onToggle={() => toggle("servant-leadership")}
      >
        {leadership.blocks.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </SupportPillar>

      {/* TEACHING — featured */}
      <FeaturedPillar
        id="teaching"
        index="06"
        label="Teaching"
        belief={teachingBelief}
        preview={teachingPreview}
        image={images.coachPortrait}
        imageAlt="Coach Elete, focused close-up"
        open={openId === "teaching"}
        onToggle={() => toggle("teaching")}
      >
        <p className="text-zinc-400 leading-relaxed max-w-prose">{teachingIntro}</p>
        <div className="mt-5 space-y-6">
          {teachingApproaches.map((c) => (
            <div key={c.id} data-testid={`teaching-item-${c.id}`}>
              <h4 className="font-heading text-lg font-semibold tracking-tight text-white">{c.title}</h4>
              {c.lead.map((l, li) => (
                <p key={li} className="font-heading text-white font-medium leading-snug">
                  {l}
                </p>
              ))}
              <p className="mt-2 text-zinc-400 leading-relaxed max-w-prose">{c.body}</p>
            </div>
          ))}
        </div>
      </FeaturedPillar>

      {/* QUOTE BREAK — Excellence */}
      <section className="relative py-24 md:py-32 overflow-hidden border-y border-white/10" data-testid="philosophy-excellence">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-ink-surface via-ink-base to-ink-base" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 text-center">
          <Reveal>
            <p className="label-eyebrow text-burnt mb-8">On Excellence</p>
            <Quote className="mx-auto text-burnt mb-6" size={34} />
            <blockquote className="font-heading text-2xl md:text-3xl lg:text-4xl font-light italic text-white leading-snug text-balance">
              “Outcomes are a distraction. Focusing on the process of what you have to do to
              get the outcome is the most important thing you need to do.”
            </blockquote>
            <cite className="mt-6 block text-gold not-italic tracking-wide">Nick Saban</cite>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-10 text-zinc-400 leading-relaxed max-w-2xl mx-auto">
              I believe success isn't something you chase. It is something that naturally
              follows when your daily habits, standards, and attitude consistently align with
              your purpose.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Next chapter → Experience (single closing action) */}
      <div className="border-t border-white/10">
        <NextChapter to="/resume" label="Explore My Experience" />
      </div>
    </div>
  );
}
